/**
 * sessionUtils — pure helpers for working with the `sessions` array.
 *
 * Why this module exists
 * ----------------------
 * The original session-id lookup lived inline in JournalView's
 * `beginAddToExisting`, where it gradually accumulated subtle bugs:
 *
 *   - `parseInt(e.target.value)` truncated string-format ids
 *     (`"1714567890123-a3f2x9"` → `1714567890123`), then strict-equality
 *     comparisons against the original string id failed → the dropdown
 *     became a no-op (see git: 81c03a8 hotfix prequel).
 *   - Mixed legacy (numeric) and current (string-with-suffix) id formats
 *     coexist in user data, so any single-type comparison is wrong.
 *
 * Pulling the lookup into a pure module:
 *
 *   1. Makes the "find session by id" contract testable in isolation
 *      (see `__tests__/sessionUtils.test.mjs`).
 *   2. Gives every future caller (not just JournalView) a single place
 *      that gets the type-coercion right.
 *   3. Makes the next bulk-rename script structurally unable to break
 *      it — the test suite catches the regression before merge.
 */

/**
 * Find a session in `sessions` whose id matches `rawId`, with type-tolerant
 * comparison so old (numeric) and new (string-with-suffix) id formats both
 * work.
 *
 * `rawId` is typically the value of an HTML `<select>` (always a string),
 * but we accept any form a caller might pass.
 *
 * @param {Array<{id: string|number}>} sessions
 * @param {string|number|null|undefined} rawId
 * @returns {object|null} The matched session object (with its original `id`
 *                       type preserved), or null if not found / no input.
 */
export const findSessionById = (sessions, rawId) => {
  if (rawId === null || rawId === undefined || rawId === '') return null;
  if (!Array.isArray(sessions) || sessions.length === 0) return null;
  const target = String(rawId);
  return sessions.find(s => s && String(s.id) === target) || null;
};
